import React, { useState, useEffect, useRef } from 'react';
import { getFileExtension, isCodeFile, isExcelFile, isImageFile, isAudioFile } from '../utils/constants';
import { getAuthToken } from '../utils/authToken';
import { useLanguage } from '../i18n';
import { userErrorMessage } from '../utils/userError';
import Modal from './ui/Modal';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

/**
 * Render priority:
 *  1. has_html → iframe srcDoc with stored HTML (Omnis-created PDFs, cached DOCX)
 *  2. is_docx without html_content → lazy-fetch /docx-html (mammoth), then #1
 *  3. is_pdf without html_content → iframe with blob URL (browser-native PDF viewer)
 *  4. is_image / is_audio → media element
 *  5. is_excel → JSON → table
 *  6. text fallback (legacy "extracted text" behavior)
 */
/**
 * Image preview body — original-resolution `<img>` plus a small zoom
 * toolbar. The previous version constrained the rendered image to
 * `max-h-[45dvh]` inside a tiny scrolling box, which made anything
 * larger than ~600px feel "blurry / downscaled" even though the
 * underlying bytes were untouched. We now:
 *   • render the image at its natural size by default (no max-height
 *     cap), inside a scrollable viewport that grows to fill the modal
 *   • offer + / − / 100% / fit-width buttons for zoom (image-element
 *     `width` style — no CSS transform pixel jitter)
 *   • offer an "Open original" link that pops the blob URL in a new
 *     tab so the user gets the browser-native viewer (full pinch zoom
 *     on touch, right-click "save image as", etc.)
 * No bytes are processed — `blobUrl` is created from the raw
 * `/download` response.
 */
const ZOOM_STEPS = [25, 50, 75, 100, 125, 150, 200, 300, 400];

const ImagePreviewBlock = ({ blobUrl, filename, content }) => {
    const { t } = useLanguage();
    const [zoom, setZoom] = useState(null); // null = "fit width", number = zoom %

    const zoomOut = () => {
        const current = zoom ?? 100;
        const idx = ZOOM_STEPS.findIndex((s) => s >= current);
        const next = ZOOM_STEPS[Math.max(0, idx - 1)] || ZOOM_STEPS[0];
        setZoom(next);
    };
    const zoomIn = () => {
        const current = zoom ?? 100;
        const idx = ZOOM_STEPS.findIndex((s) => s > current);
        const next = ZOOM_STEPS[idx === -1 ? ZOOM_STEPS.length - 1 : idx];
        setZoom(next);
    };
    const reset = () => setZoom(100);
    const fitWidth = () => setZoom(null);

    return (
        <div className="space-y-3">
            <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-lg p-3 text-sm">
                <div className="flex items-start gap-2 text-teal-800 dark:text-teal-300">
                    <span className="text-lg">🔍</span>
                    <div className="flex-1">
                        <div className="font-medium">{t('fmOriginalImageHint', 'Original image · zoom or open in a new tab for full fidelity')}</div>
                        <div className="text-teal-700 dark:text-teal-400 text-xs mt-1">
                            {t('fmOcrExtractedHint', 'Text below was extracted by Gemini Vision OCR — the image bytes are unmodified.')}
                        </div>
                    </div>
                </div>
            </div>

            {blobUrl ? (
                <>
                    <div className="flex items-center gap-1.5 text-xs">
                        <button
                            onClick={zoomOut}
                            disabled={(zoom ?? 100) <= ZOOM_STEPS[0]}
                            className="px-2 py-1 border border-pai rounded bg-white/[0.03] text-pai-secondary hover:bg-white/[0.07] disabled:opacity-40"
                            title={t('fmZoomOut', 'Zoom out')}
                        >
                            −
                        </button>
                        <button
                            onClick={zoomIn}
                            disabled={(zoom ?? 100) >= ZOOM_STEPS[ZOOM_STEPS.length - 1]}
                            className="px-2 py-1 border border-pai rounded bg-white/[0.03] text-pai-secondary hover:bg-white/[0.07] disabled:opacity-40"
                            title={t('fmZoomIn', 'Zoom in')}
                        >
                            +
                        </button>
                        <button
                            onClick={reset}
                            className="px-2 py-1 border border-pai rounded bg-white/[0.03] text-pai-secondary hover:bg-white/[0.07]"
                            title={t('fmActualSize', 'Actual size')}
                        >
                            100%
                        </button>
                        <button
                            onClick={fitWidth}
                            className={`px-2 py-1 border rounded ${
                                zoom === null
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                    : 'border-pai bg-white/[0.03] text-pai-secondary hover:bg-white/[0.07]'
                            }`}
                            title={t('fmFitToWidth', 'Fit to width')}
                        >
                            {t('fmFit', 'Fit')}
                        </button>
                        <div className="flex-1" />
                        <span className="text-pai-meta tabular-nums">
                            {zoom === null ? t('fmFitWidth', 'fit width') : `${zoom}%`}
                        </span>
                        <a
                            href={blobUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2 py-1 border border-pai rounded bg-white/[0.03] text-blue-600 dark:text-blue-400 hover:bg-white/[0.07]"
                            title={t('fmOpenOriginalHint', 'Open the original image in a new tab')}
                        >
                            {t('fmOpenOriginal', 'Open original ↗')}
                        </a>
                    </div>
                    <div className="bg-white/[0.05] rounded-lg border border-pai-subtle overflow-auto" style={{ maxHeight: '70dvh' }}>
                        <div className="flex justify-center p-4">
                            <img
                                src={blobUrl}
                                alt={filename}
                                style={zoom === null
                                    ? { maxWidth: '100%', height: 'auto' }
                                    : { width: `${zoom}%`, height: 'auto' }}
                                className="rounded"
                            />
                        </div>
                    </div>
                </>
            ) : (
                <div className="text-xs text-pai-faint italic text-center py-2">{t('fmLoadingImage', 'Loading image…')}</div>
            )}

            {content && content.trim() ? (
                <div>
                    <h4 className="text-sm font-medium text-pai-meta mb-2">{t('fmExtractedTextOcr', 'Extracted Text (OCR)')}</h4>
                    <div className="bg-white/[0.025] rounded-lg border border-pai-subtle p-4 max-h-[30dvh] overflow-auto">
                        <pre className="text-sm whitespace-pre-wrap break-words text-white text-pai-secondary font-sans">{content}</pre>
                    </div>
                </div>
            ) : (
                <div className="text-sm text-pai-faint italic text-center py-4">
                    {t('fmNoTextExtractedImage', 'No text could be extracted from this image.')}
                </div>
            )}
        </div>
    );
};

// `zClass` (#440): a caller that itself sits above z-[70] (the Finance ticket
// detail is z-[80]) raises the preview so it is not hidden behind it.
const DocumentPreviewModal = ({ documentId, documentName, onClose, zClass = 'z-[70]' }) => {
    const { t } = useLanguage();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [htmlPreview, setHtmlPreview] = useState(null);     // string srcDoc
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);       // blob URL for native PDF viewer
    const [mediaBlobUrl, setMediaBlobUrl] = useState(null);   // image/audio playback
    const blobUrlRef = useRef(null);
    const pdfBlobUrlRef = useRef(null);

    // The data-fetch effect must NEVER depend on `onClose`. Callers pass an inline arrow
    // (`onClose={() => setPreview(null)}`), so a NEW function is created on every
    // parent re-render. When the composer's audio-recording timer re-renders the
    // (non-memoized) sidebar/Conversation-Data-card once per second, that churned
    // `onClose` used to be a dependency of the fetch effect below — so the effect
    // re-ran every tick: setLoading(true) re-showed the "Loading preview…" spinner,
    // the blob URLs were revoked + recreated, and the iframe/image reloaded. That
    // is the #220 "flicker loop". The fetch is keyed on `documentId` alone, so a
    // recording/transcription re-render can never restart it or remount the
    // content. (Bug family of "Disco Night".) Escape is owned by <Modal>, which
    // reads the latest onClose through a ref for the same reason, and closes
    // ONLY this preview when it is the topmost dialog.

    useEffect(() => {
        if (!documentId) return;

        let cancelled = false;
        const token = getAuthToken();
        const authHeaders = { Authorization: `Bearer ${token}` };

        const run = async () => {
            try {
                setLoading(true);
                setError(null);
                setHtmlPreview(null);
                setPdfBlobUrl(null);
                setMediaBlobUrl(null);

                const previewResp = await fetch(`${API_BASE}/api/documents/${documentId}/preview`, { headers: authHeaders });
                if (!previewResp.ok) throw new Error(t('fmLoadPreviewFailed', 'Failed to load preview'));
                const data = await previewResp.json();
                if (cancelled) return;
                setPreviewData(data);

                // Decide which secondary fetch (if any) to kick off
                const fetchHtml = async (url) => {
                    const r = await fetch(url, { headers: authHeaders });
                    if (!r.ok) throw new Error(`HTML preview HTTP ${r.status}`);
                    return r.text();
                };

                const fetchBlob = async (url) => {
                    const r = await fetch(url, { headers: authHeaders });
                    if (!r.ok) throw new Error(`Blob HTTP ${r.status}`);
                    return r.blob();
                };

                // 1. Stored HTML (Omnis-created PDFs, cached DOCX)
                if (data.has_html) {
                    try {
                        const html = await fetchHtml(`${API_BASE}/api/documents/${documentId}/html`);
                        if (!cancelled) setHtmlPreview(html);
                    } catch (e) {
                        console.warn('Could not load stored HTML, falling back', e);
                    }
                }
                // 2. DOCX without cached HTML → mammoth on demand
                else if (data.is_docx) {
                    try {
                        const html = await fetchHtml(`${API_BASE}/api/documents/${documentId}/docx-html`);
                        if (!cancelled) setHtmlPreview(html);
                    } catch (e) {
                        console.warn('DOCX→HTML conversion unavailable, falling back to text', e);
                    }
                }
                // 3. Uploaded PDF without HTML → browser-native viewer via blob
                else if (data.is_pdf) {
                    try {
                        const blob = await fetchBlob(`${API_BASE}/api/documents/${documentId}/download`);
                        if (cancelled) return;
                        const url = URL.createObjectURL(blob);
                        pdfBlobUrlRef.current = url;
                        setPdfBlobUrl(url);
                    } catch (e) {
                        console.warn('PDF blob fetch failed, falling back to text', e);
                    }
                }

                // Media playback (orthogonal to the above)
                if (data.is_audio || data.is_image) {
                    try {
                        const blob = await fetchBlob(`${API_BASE}/api/documents/${documentId}/download`);
                        if (cancelled) return;
                        const url = URL.createObjectURL(blob);
                        blobUrlRef.current = url;
                        setMediaBlobUrl(url);
                    } catch (e) {
                        console.warn('Could not load media for playback:', e);
                    }
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Error loading preview:', err);
                    setError(userErrorMessage(err, t));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        run();

        return () => {
            cancelled = true;
            if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
            if (pdfBlobUrlRef.current) URL.revokeObjectURL(pdfBlobUrlRef.current);
            blobUrlRef.current = null;
            pdfBlobUrlRef.current = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see note above: 't' is intentionally omitted
    }, [documentId]);

    const getFileIcon = (filename) => {
        const ext = getFileExtension(filename);
        const iconMap = {
            pdf: '📕', doc: '📘', docx: '📘', pptx: '📙',
            xls: '📊', xlsx: '📊', txt: '📄', md: '📝',
            py: '🐍', js: '📜', jsx: '⚛️', ts: '📘', tsx: '⚛️',
            json: '📋', csv: '📊', xml: '📋', html: '🌐', css: '🎨',
        };
        return iconMap[ext] || '📄';
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const parseExcelData = (content) => {
        const strategies = [
            (c) => JSON.parse(c),
            (c) => JSON.parse(c.replace(/\n\n/g, '\n').trim()),
            (c) => {
                const trimmed = c.trim();
                if (trimmed.startsWith('{')) {
                    const lastBrace = trimmed.lastIndexOf('}');
                    if (lastBrace > 0) return JSON.parse(trimmed.substring(0, lastBrace + 1));
                } else if (trimmed.startsWith('[')) {
                    const lastBracket = trimmed.lastIndexOf(']');
                    if (lastBracket > 0) return JSON.parse(trimmed.substring(0, lastBracket + 1));
                }
                throw new Error('No JSON structure found');
            },
        ];

        for (const strategy of strategies) {
            try {
                const data = strategy(content);
                if (Array.isArray(data) && data.length > 0) return { "Sheet1": data };
                if (data && typeof data === 'object' && Object.keys(data).length > 0) {
                    const values = Object.values(data);
                    if (values.some(v => Array.isArray(v))) return data;
                }
            } catch (e) { /* try next */ }
        }
        return null;
    };

    const renderExcelTable = (sheetName, rows) => {
        if (!rows || rows.length === 0) return null;
        const headers = Object.keys(rows[0]);

        return (
            <div key={sheetName} className="mb-6">
                <h4 className="text-md font-semibold text-pai-secondary mb-3 flex items-center gap-2">
                    <span>📊</span><span>{sheetName}</span>
                </h4>
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white/[0.03] border border-pai text-sm">
                        <thead className="bg-blue-50 dark:bg-blue-900/20">
                            <tr>
                                {headers.map((header, idx) => (
                                    <th key={idx} className="px-4 py-2 border-b border-r border-pai text-left font-semibold text-pai-secondary whitespace-nowrap">
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, rowIdx) => (
                                <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white/[0.03]' : 'bg-white/[0.05] bg-white/[0.06]'}>
                                    {headers.map((header, colIdx) => (
                                        <td key={colIdx} className="px-4 py-2 border-b border-r border-pai text-white text-pai-secondary whitespace-nowrap">
                                            {row[header] !== null && row[header] !== undefined ? String(row[header]) : ''}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="mt-2 text-xs text-pai-meta">
                    {t('fmRowsByColumns', '{rows} row{rs} × {cols} column{cs}', {
                        rows: rows.length,
                        rs: rows.length !== 1 ? 's' : '',
                        cols: headers.length,
                        cs: headers.length !== 1 ? 's' : '',
                    })}
                </div>
            </div>
        );
    };

    const renderContent = () => {
        if (!previewData) return null;

        const { content, filename } = previewData;
        const isCode = isCodeFile(filename);
        const isExcel = isExcelFile(filename);
        const isImage = isImageFile(filename);
        const isAudio = isAudioFile(filename);

        // 1. HTML preview (Omnis-created PDFs, mammoth-converted DOCX) — full-fidelity iframe
        if (htmlPreview) {
            return (
                <iframe
                    title={filename}
                    srcDoc={htmlPreview}
                    sandbox="allow-same-origin"
                    className="w-full h-[75dvh] bg-white rounded border border-pai-subtle"
                />
            );
        }

        // 2. Uploaded PDF without HTML → browser-native PDF viewer (Chrome/Firefox embed)
        if (pdfBlobUrl) {
            return (
                <iframe
                    title={filename}
                    src={pdfBlobUrl}
                    className="w-full h-[75dvh] bg-white/[0.05] rounded border border-pai-subtle"
                />
            );
        }

        // 3. Audio
        if (isAudio) {
            return (
                <div className="space-y-3">
                    <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-3 text-sm">
                        <div className="flex items-start gap-2 text-purple-800 dark:text-purple-300">
                            <span className="text-lg">🎵</span>
                            <div>
                                <div className="font-medium">{t('fmAudioTranscriptionTitle', 'Audio with AI-Generated Transcription')}</div>
                                <div className="text-purple-700 dark:text-purple-400 text-xs mt-1">
                                    {t('fmAudioTranscriptionHint', 'Audio was transcribed using Gemini Audio API. The original audio and transcription are shown below.')}
                                </div>
                            </div>
                        </div>
                    </div>
                    {mediaBlobUrl ? (
                        <div className="flex justify-center bg-white/[0.05] rounded-lg border border-pai-subtle p-4">
                            <audio controls style={{ width: '100%', maxWidth: '500px' }} src={mediaBlobUrl}>
                                {t('fmAudioNotSupported', 'Your browser does not support the audio element.')}
                            </audio>
                        </div>
                    ) : (
                        <div className="text-xs text-pai-faint italic text-center py-2">{t('fmLoadingAudioPlayer', 'Loading audio player...')}</div>
                    )}
                    {content && content.trim() ? (
                        <div>
                            <h4 className="text-sm font-medium text-pai-meta mb-2">{t('fmTranscription', 'Transcription')}</h4>
                            <div className="bg-white/[0.025] rounded-lg border border-pai-subtle p-4 max-h-[40dvh] overflow-auto">
                                <pre className="text-sm whitespace-pre-wrap break-words text-white text-pai-secondary font-sans">{content}</pre>
                            </div>
                        </div>
                    ) : (
                        <div className="text-sm text-pai-faint italic text-center py-4">
                            {t('fmNoTranscriptionAvailable', 'No transcription available for this audio file.')}
                        </div>
                    )}
                </div>
            );
        }

        // 4. Image
        if (isImage) {
            return (
                <ImagePreviewBlock
                    blobUrl={mediaBlobUrl}
                    filename={filename}
                    content={content}
                />
            );
        }

        // 5. Excel — JSON → table view
        if (isExcel) {
            const excelData = parseExcelData(content);
            if (excelData && typeof excelData === 'object') {
                return (
                    <div className="space-y-3">
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 text-sm">
                            <div className="flex items-start gap-2 text-blue-800 dark:text-blue-300">
                                <span className="text-lg">ℹ️</span>
                                <div>
                                    <div className="font-medium">{t('fmSpreadsheetPreview', 'Spreadsheet Preview')}</div>
                                    <div className="text-blue-700 dark:text-blue-400 text-xs mt-1">
                                        {t('fmSpreadsheetPreviewHint', 'Showing data as a table. Download the file to see formulas, formatting, and charts.')}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white/[0.025] rounded border border-pai-subtle p-4 max-h-[55dvh] overflow-auto">
                            {Object.entries(excelData).map(([sheetName, rows]) => renderExcelTable(sheetName, rows))}
                        </div>
                    </div>
                );
            }
            // Fallback to raw JSON
            return (
                <div className="bg-white/[0.025] rounded border border-pai-subtle p-4 max-h-[55dvh] overflow-auto">
                    <pre className="text-xs font-mono text-white text-pai-secondary whitespace-pre-wrap break-words">{content}</pre>
                </div>
            );
        }

        // 6. Last-resort text view (covers .txt, .md, code, and any path that
        // didn't get an HTML/PDF render — unusual but possible)
        return (
            <div className="bg-white/[0.025] rounded border border-pai-subtle p-4 max-h-[60dvh] overflow-auto">
                <pre className={`text-sm whitespace-pre-wrap break-words ${isCode ? 'font-mono text-white text-pai-secondary' : 'text-white text-pai-secondary'}`}>
                    {content}
                </pre>
            </div>
        );
    };

    // <Modal> portals to document.body so the overlay escapes ANY transformed / overflow-
    // hidden ancestor. The two callsites that trapped it — the DocumentsModal box
    // (which retains a `transform: translateY(0)` from its `forwards` open
    // animation and clips with `overflow-hidden`, #225 "Preview File Manager") and
    // the ConversationDataCard (which uses `-translate-y-1/2`, #221 "Conversation
    // Modal Preview size") — both establish a containing block for `position:
    // fixed`, so an in-tree `fixed inset-0` was sized to the small parent box
    // instead of the viewport. Rendering into <body> (no transformed ancestor)
    // makes `fixed inset-0` viewport-relative again, so the preview opens at a
    // real document-review size for every caller (chat/ticket/expense/FM/trash/
    // conversation-data) without per-callsite hacks. z-[70] sits above the
    // z-[60] Documents/Settings modal chrome.
    return (
        <Modal
            onClose={onClose}
            zIndex={zClass}
            size="none"
            layout="column"
            className="sm:p-6"
            cardClassName="w-[90vw] max-w-6xl h-[90dvh] max-h-[90dvh]"
            cardTestId="document-preview-modal"
        >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-pai-subtle">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-2xl">{getFileIcon(documentName)}</span>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-white truncate">
                                {documentName}
                            </h3>
                            {previewData && (
                                <div className="flex gap-4 text-xs text-pai-meta mt-1">
                                    <span>{t('fmSizeLabel', 'Size: {size}', { size: formatFileSize(previewData.file_size) })}</span>
                                    {!htmlPreview && !pdfBlobUrl && (
                                        <span>{t('fmContentExtractedLabel', 'Content: {size} extracted', { size: formatFileSize(previewData.content_length) })}</span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-pai-meta hover:text-white  text-2xl leading-none ml-4"
                        title={t('fmCloseEsc', 'Close (Esc)')}
                    >
                        ×
                    </button>
                </div>

                {/* Body */}
                <div data-testid="document-preview-body" className="flex-1 min-h-0 overflow-auto overscroll-contain p-4">
                    {loading && (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                            <span className="ml-3 text-pai-meta">{t('fmLoadingPreviewEllipsis', 'Loading preview...')}</span>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 text-red-700 dark:text-red-300">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-xl">⚠️</span>
                                <span className="font-semibold">{t('fmPreviewError', 'Preview Error')}</span>
                            </div>
                            <p>{error}</p>
                        </div>
                    )}

                    {!loading && !error && renderContent()}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-pai-subtle flex justify-between items-center bg-white/[0.06]">
                    <div className="text-xs text-pai-meta">
                        {htmlPreview
                            ? t('fmFullFidelityHtmlPreview', 'Full-fidelity HTML preview')
                            : pdfBlobUrl
                                ? t('fmPdfPreview', 'PDF preview')
                                : t('fmExtractedTextFallback', 'Showing extracted text content from the document')}
                    </div>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600 text-sm"
                    >
                        {t('commonClose', 'Close')}
                    </button>
                </div>
            </Modal>
    );
};

export default DocumentPreviewModal;
